/**
 * Kotwice ubranek per (rasa, poza) — PLAN-V2 §2.4.
 * Fallback: wpis rasy → domyślne archetypu → twardy DEFAULT_POSE.
 * Gra nigdy nie czeka na kalibrację.
 */
import { DOGS, type BodyArchetype, type DogBreedId } from '@pieskowo/shared';

/** Punkt kotwicy [x, y] w przestrzeni sprite'a (~128 px, pivot w flipPivot). */
export type AnchorPoint = readonly [number, number];

export type AnchorPose = {
  head: AnchorPoint;
  neck: AnchorPoint;
  back: AnchorPoint;
  mouth: AnchorPoint;
  scale: number;
  flipPivot: number;
};

export type PoseName =
  | 'idle'
  | 'walkA'
  | 'walkB'
  | 'sit'
  | 'jump'
  | 'wave'
  | 'eat'
  | 'swim'
  | 'sleep'
  | 'happy';

export const ALL_POSES: readonly PoseName[] = [
  'idle',
  'walkA',
  'walkB',
  'sit',
  'jump',
  'wave',
  'eat',
  'swim',
  'sleep',
  'happy',
] as const;

/** Slot stroju → nazwa kotwicy. */
export const SLOT_ANCHOR: Record<string, keyof Pick<AnchorPose, 'head' | 'neck' | 'back' | 'mouth'>> = {
  hat: 'head',
  bow: 'head',
  glasses: 'head',
  cap: 'head',
  collar: 'neck',
  body: 'back',
  suit: 'back',
  shoes: 'back',
  accessory: 'back',
};

/** Buty tylko w idle/sit (decyzja V1). */
export function shoesVisibleForPose(pose: string): boolean {
  return pose === 'idle' || pose === 'sit';
}

/** Twarde minimum — zawsze wygląda „ok". */
export const DEFAULT_POSE: AnchorPose = {
  head: [64, 30],
  neck: [64, 52],
  back: [64, 68],
  mouth: [82, 48],
  scale: 1,
  flipPivot: 64,
};

/** Rozsądne domyślne per archetyp (nie puste). */
export const ARCHETYPE_DEFAULTS: Record<BodyArchetype, Record<PoseName, AnchorPose>> = {
  arch_small_fluffy: {
    idle: { head: [64, 28], neck: [64, 50], back: [64, 66], mouth: [80, 46], scale: 1, flipPivot: 64 },
    walkA: { head: [62, 26], neck: [60, 48], back: [58, 64], mouth: [80, 44], scale: 1, flipPivot: 64 },
    walkB: { head: [66, 26], neck: [64, 48], back: [62, 64], mouth: [84, 44], scale: 1, flipPivot: 64 },
    sit: { head: [64, 36], neck: [64, 56], back: [64, 72], mouth: [78, 52], scale: 1, flipPivot: 64 },
    jump: { head: [64, 22], neck: [64, 44], back: [64, 60], mouth: [80, 40], scale: 1.05, flipPivot: 64 },
    wave: { head: [64, 28], neck: [64, 50], back: [64, 66], mouth: [80, 46], scale: 1, flipPivot: 64 },
    eat: { head: [70, 40], neck: [66, 56], back: [64, 70], mouth: [86, 54], scale: 1, flipPivot: 64 },
    swim: { head: [64, 34], neck: [64, 52], back: [64, 64], mouth: [80, 48], scale: 0.95, flipPivot: 64 },
    sleep: { head: [58, 48], neck: [60, 58], back: [64, 70], mouth: [72, 56], scale: 0.98, flipPivot: 64 },
    happy: { head: [64, 24], neck: [64, 48], back: [64, 64], mouth: [80, 42], scale: 1.08, flipPivot: 64 },
  },
  arch_long_low: {
    idle: { head: [72, 36], neck: [64, 52], back: [52, 60], mouth: [90, 48], scale: 1, flipPivot: 64 },
    walkA: { head: [70, 34], neck: [62, 50], back: [50, 58], mouth: [88, 46], scale: 1, flipPivot: 64 },
    walkB: { head: [74, 34], neck: [66, 50], back: [54, 58], mouth: [92, 46], scale: 1, flipPivot: 64 },
    sit: { head: [70, 42], neck: [64, 56], back: [54, 66], mouth: [86, 52], scale: 1, flipPivot: 64 },
    jump: { head: [72, 28], neck: [64, 46], back: [52, 54], mouth: [90, 40], scale: 1.05, flipPivot: 64 },
    wave: { head: [72, 36], neck: [64, 52], back: [52, 60], mouth: [90, 48], scale: 1, flipPivot: 64 },
    eat: { head: [78, 44], neck: [68, 56], back: [54, 64], mouth: [94, 54], scale: 1, flipPivot: 64 },
    swim: { head: [72, 40], neck: [64, 54], back: [52, 58], mouth: [90, 50], scale: 0.95, flipPivot: 64 },
    sleep: { head: [66, 50], neck: [60, 58], back: [52, 66], mouth: [80, 56], scale: 0.98, flipPivot: 64 },
    happy: { head: [72, 30], neck: [64, 48], back: [52, 58], mouth: [90, 42], scale: 1.08, flipPivot: 64 },
  },
  arch_medium: {
    idle: { head: [64, 26], neck: [64, 48], back: [64, 70], mouth: [84, 44], scale: 1.05, flipPivot: 64 },
    walkA: { head: [62, 24], neck: [60, 46], back: [60, 68], mouth: [82, 42], scale: 1.05, flipPivot: 64 },
    walkB: { head: [66, 24], neck: [64, 46], back: [64, 68], mouth: [86, 42], scale: 1.05, flipPivot: 64 },
    sit: { head: [64, 34], neck: [64, 54], back: [64, 74], mouth: [82, 50], scale: 1.05, flipPivot: 64 },
    jump: { head: [64, 18], neck: [64, 40], back: [64, 62], mouth: [84, 36], scale: 1.1, flipPivot: 64 },
    wave: { head: [64, 26], neck: [64, 48], back: [64, 70], mouth: [84, 44], scale: 1.05, flipPivot: 64 },
    eat: { head: [72, 38], neck: [66, 54], back: [64, 72], mouth: [90, 52], scale: 1.05, flipPivot: 64 },
    swim: { head: [64, 32], neck: [64, 50], back: [64, 66], mouth: [84, 46], scale: 1, flipPivot: 64 },
    sleep: { head: [58, 46], neck: [60, 56], back: [64, 72], mouth: [74, 54], scale: 1.02, flipPivot: 64 },
    happy: { head: [64, 20], neck: [64, 44], back: [64, 66], mouth: [84, 38], scale: 1.12, flipPivot: 64 },
  },
};

export type AnchorsFile = {
  /** Opcjonalne nadpisania per rasa (klucze = DogBreedId lub string). */
  [breedOrMeta: string]: Record<string, Partial<AnchorPose> | unknown> | unknown;
};

function isPoint(v: unknown): v is AnchorPoint {
  return (
    Array.isArray(v) &&
    v.length >= 2 &&
    typeof v[0] === 'number' &&
    typeof v[1] === 'number' &&
    Number.isFinite(v[0]) &&
    Number.isFinite(v[1])
  );
}

function mergePose(base: AnchorPose, partial: Partial<AnchorPose> | null | undefined): AnchorPose {
  if (!partial || typeof partial !== 'object') return { ...base, head: [...base.head], neck: [...base.neck], back: [...base.back], mouth: [...base.mouth] };
  return {
    head: isPoint(partial.head) ? [partial.head[0], partial.head[1]] : [...base.head],
    neck: isPoint(partial.neck) ? [partial.neck[0], partial.neck[1]] : [...base.neck],
    back: isPoint(partial.back) ? [partial.back[0], partial.back[1]] : [...base.back],
    mouth: isPoint(partial.mouth) ? [partial.mouth[0], partial.mouth[1]] : [...base.mouth],
    scale: typeof partial.scale === 'number' && Number.isFinite(partial.scale) ? partial.scale : base.scale,
    flipPivot:
      typeof partial.flipPivot === 'number' && Number.isFinite(partial.flipPivot)
        ? partial.flipPivot
        : base.flipPivot,
  };
}

function parsePosePartial(raw: unknown): Partial<AnchorPose> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const out: Partial<AnchorPose> = {};
  if (isPoint(o.head)) out.head = [o.head[0], o.head[1]];
  if (isPoint(o.neck)) out.neck = [o.neck[0], o.neck[1]];
  if (isPoint(o.back)) out.back = [o.back[0], o.back[1]];
  if (isPoint(o.mouth)) out.mouth = [o.mouth[0], o.mouth[1]];
  if (typeof o.scale === 'number' && Number.isFinite(o.scale)) out.scale = o.scale;
  if (typeof o.flipPivot === 'number' && Number.isFinite(o.flipPivot)) out.flipPivot = o.flipPivot;
  return out;
}

/** Parsuj JSON (string lub obiekt) — zepsuty input → pusty słownik (bez crasha). */
export function parseAnchorsFile(raw: unknown): Record<string, Record<string, Partial<AnchorPose>>> {
  let root: unknown = raw;
  if (typeof raw === 'string') {
    try {
      root = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!root || typeof root !== 'object' || Array.isArray(root)) return {};
  const out: Record<string, Record<string, Partial<AnchorPose>>> = {};
  for (const [breed, poses] of Object.entries(root as Record<string, unknown>)) {
    if (!poses || typeof poses !== 'object' || Array.isArray(poses)) continue;
    const poseMap: Record<string, Partial<AnchorPose>> = {};
    for (const [pose, val] of Object.entries(poses as Record<string, unknown>)) {
      const p = parsePosePartial(val);
      if (p) poseMap[pose] = p;
    }
    out[breed] = poseMap;
  }
  return out;
}

export function archetypeForBreed(breed: string): BodyArchetype {
  const def = DOGS[breed as DogBreedId];
  return def?.archetype ?? 'arch_medium';
}

/**
 * Rozwiąż kotwice: override z pliku → archetyp → DEFAULT_POSE.
 * Zawsze zwraca pełny AnchorPose (nigdy null).
 */
export function resolveAnchors(
  file: Record<string, Record<string, Partial<AnchorPose>>> | null | undefined,
  breed: string,
  pose: string,
): AnchorPose {
  const arch = archetypeForBreed(breed);
  const poseKey = (ALL_POSES as readonly string[]).includes(pose) ? (pose as PoseName) : 'idle';
  const archBase = ARCHETYPE_DEFAULTS[arch][poseKey] ?? ARCHETYPE_DEFAULTS[arch].idle;
  const base = mergePose(DEFAULT_POSE, archBase);

  const breedMap = file?.[breed];
  const override = breedMap?.[poseKey] ?? breedMap?.[pose] ?? null;
  if (!override) {
    // brak rasy w pliku — spróbuj idle rasy + scale z archetypu
    const idleOverride = breedMap?.idle ?? null;
    if (idleOverride && poseKey !== 'idle') {
      // poza bez wpisu: bazuj na archetypie (już w base), nie mieszaj idle rasy
      return base;
    }
    return mergePose(base, idleOverride);
  }
  return mergePose(base, override);
}

/** Lokalne współrzędne kontenera (środek = 0,0) z kotwicy sprite-space. */
export function anchorToLocal(
  pose: AnchorPose,
  key: keyof Pick<AnchorPose, 'head' | 'neck' | 'back' | 'mouth'>,
  flipX = false,
): { x: number; y: number } {
  const [ax, ay] = pose[key];
  const pivot = pose.flipPivot;
  let x = ax - pivot;
  const y = ay - pivot;
  if (flipX) x = -x;
  return { x, y };
}

/** Offsety slotów względem kotwicy (drobne korekty wizualne). */
export function slotLocalOffset(slot: string): { x: number; y: number } {
  switch (slot) {
    case 'hat':
    case 'cap':
      return { x: 0, y: -8 };
    case 'bow':
      return { x: 10, y: -4 };
    case 'glasses':
      return { x: 0, y: 4 };
    case 'collar':
      // lekko w dół od kotwicy neck — obroża „siada" na szyi
      return { x: 0, y: 4 };
    case 'body':
    case 'suit':
      return { x: 0, y: 4 };
    case 'shoes':
      return { x: 0, y: 22 };
    case 'accessory':
      return { x: 14, y: 8 };
    default:
      return { x: 0, y: 0 };
  }
}

/**
 * Zbuduj pełny plik anchors z domyślnymi per rasa (= archetyp).
 * Używane jako seed i do eksportu w trybie ?anchors=1.
 */
export function buildDefaultAnchorsFile(): Record<string, Record<PoseName, AnchorPose>> {
  const out: Record<string, Record<PoseName, AnchorPose>> = {};
  for (const breed of Object.keys(DOGS) as DogBreedId[]) {
    const arch = DOGS[breed].archetype;
    const poses = {} as Record<PoseName, AnchorPose>;
    for (const p of ALL_POSES) {
      poses[p] = mergePose(DEFAULT_POSE, ARCHETYPE_DEFAULTS[arch][p]);
    }
    out[breed] = poses;
  }
  return out;
}
